/**
 * Tema visual.
 *
 * La referencia es la vista de grafo de Obsidian: lienzo oscuro y limpio,
 * enlaces finos y translucidos que no compiten con los nodos, y nodos con un
 * resplandor suave en vez de cajas duras.
 *
 * Sobre los colores del sexo hay una restriccion que no es estetica: tiene
 * que funcionar para alguien que no distinga bien los colores. Por eso se
 * usan SIEMPRE las dos cosas a la vez, forma y color, y el par elegido es
 * azul frio contra ambar calido, que se distinguen en los tres tipos
 * habituales de daltonismo. Nada de rojo contra verde.
 *
 * Y por si el color se pierde del todo, las formas tambien se diferencian por
 * silueta y no solo por el radio de la esquina: rectangulo para hombre,
 * capsula para mujer, rombo para desconocido.
 */
import type { Sexo } from "../core/model.js";

export type Forma = "rectangulo" | "capsula" | "rombo";

export interface EstiloSexo {
  forma: Forma;
  /** Relleno de la tarjeta. */
  relleno: number;
  /** Borde. */
  borde: number;
  /** Color del resplandor. */
  resplandor: number;
  /** Color del texto principal. */
  texto: number;
  /** Nombre legible, para la leyenda. */
  etiqueta: string;
}

export const LIENZO = 0x0e0e12;

/** Linea que marca el eje de cada generacion. Muy tenue. */
export const REJILLA = 0x1a1a22;
export const REJILLA_ALFA = 0.5;

/**
 * Banda de fondo de cada generacion, alternando.
 *
 * Es casi invisible de cerca y decisiva de lejos: cuando se ve el arbol
 * entero y las personas son puntos de nueve pixeles, la banda es lo unico
 * que sigue diciendo donde empieza una generacion y acaba la anterior.
 */
export const BANDA = 0x161620;
export const BANDA_ALFA = 0.55;

export const ENLACE = 0x9aa3b2;
export const ENLACE_ALFA = 0.17;
export const ENLACE_GROSOR = 1.4;

/** El vinculo de pareja se dibuja algo mas marcado que el de filiacion. */
export const VINCULO = 0xb8c2d4;
export const VINCULO_ALFA = 0.3;
export const VINCULO_GROSOR = 1.8;

/** Al resaltar: lo que esta conectado sube, el resto baja a esto. */
export const ALFA_ATENUADO = 0.16;
export const ENLACE_RESALTADO = 0xf0f4ff;
export const ENLACE_RESALTADO_ALFA = 0.72;

export const SELECCION = 0xffd98a;
/**
 * Color de la etiqueta de parentesco que va bajo el nombre.
 * Azul claro y en cursiva, para que se lea como una anotacion y no se
 * confunda con el nombre.
 */
export const RELACION = 0x8fb3e8;
/** Color de la etiqueta de la persona elegida como punto de perspectiva. */
export const RELACION_PERSPECTIVA = 0xffd98a;
export const TEXTO_SECUNDARIO = 0x9aa3b2;
export const TEXTO_TENUE = 0x6b7280;

const ESTILOS: Record<Sexo, EstiloSexo> = {
  // Hombre: tono frio, forma rectangular.
  M: {
    forma: "rectangulo",
    relleno: 0x16243c,
    borde: 0x5b8dd9,
    resplandor: 0x5b8dd9,
    texto: 0xdce9ff,
    etiqueta: "hombre",
  },
  // Mujer: tono calido, forma redondeada.
  F: {
    forma: "capsula",
    relleno: 0x3a2a16,
    borde: 0xd99f5b,
    resplandor: 0xd99f5b,
    texto: 0xffeeda,
    etiqueta: "mujer",
  },
  // Desconocido: gris neutro y una tercera silueta. El arbol de hoy no tiene
  // ninguno, pero una importacion futura puede traerlos.
  U: {
    forma: "rombo",
    relleno: 0x24262b,
    borde: 0x8a8f98,
    resplandor: 0x8a8f98,
    texto: 0xd8dbe0,
    etiqueta: "sexo desconocido",
  },
  // Gramps admite tambien X. Se trata igual que desconocido.
  X: {
    forma: "rombo",
    relleno: 0x24262b,
    borde: 0x8a8f98,
    resplandor: 0x8a8f98,
    texto: 0xd8dbe0,
    etiqueta: "otro",
  },
};

export function estiloDe(sexo: Sexo): EstiloSexo {
  return ESTILOS[sexo] ?? (ESTILOS.U as EstiloSexo);
}

export const LEYENDA_SEXO = [ESTILOS.M, ESTILOS.F, ESTILOS.U];

/**
 * Color de acento por apellido, para agrupar ramas de un vistazo cuando se
 * esta muy alejado y no hay texto.
 *
 * Es una paleta de tonos bien separados en el circulo cromatico, calculada y
 * no escrita a mano, para que no haya que mantener una lista cuando aparezcan
 * apellidos nuevos. Se mantiene fuera de los rojos y verdes puros para no
 * chocar con la restriccion de arriba.
 */
export function colorDeApellido(apellido: string): number {
  if (!apellido) return 0x6b7280;
  let h = 0;
  for (let i = 0; i < apellido.length; i++) {
    h = (h * 31 + apellido.charCodeAt(i)) | 0;
  }
  // Se recorre el circulo evitando el rojo-verde (0-140 grados).
  const tono = 150 + (Math.abs(h) % 210);
  return hslAHex(tono, 0.42, 0.62);
}

function hslAHex(h: number, s: number, l: number): number {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (h % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  const v = (t: number): number => Math.round(Math.min(1, Math.max(0, t + m)) * 255);
  return (v(r) << 16) | (v(g) << 8) | v(b);
}

/** Tipografia. Se usa la del sistema: no se descarga nada de la red. */
export const FUENTE =
  '"Segoe UI", "Inter", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';
